export type PlanKey = 'starter' | 'growth' | 'scale' | 'enterprise'

export interface PlanLimits {
  staff_seats: number | null
  active_cases: number | null
  storage_gb: number | null
  workflow_templates: number | null
}

export interface Plan {
  key: PlanKey
  name: string
  tagline: string
  price_aud: number | null
  billing: 'monthly' | 'contract'
  /** Marketing emphasis ("Most popular"). */
  highlight?: boolean
  /** Whether this plan can be bought by card (a Stripe price is configured). */
  stripe_price_id?: string | null
  limits: PlanLimits
  /** Human bullet points for pricing cards. */
  highlights?: string[]
  /** Machine flags used for gating. */
  features: string[]
  features_not_included: string[]
}

/**
 * Plans are defined server-side in config/plans.php and served by
 * GET /api/public/plans. Single source of truth — UI never invents plan
 * data. Cached via useState so we fetch once per session.
 */
export const usePlans = () => {
  const api = useApi()
  const state = useState<Plan[] | null>('plans', () => null)
  const loading = useState<boolean>('plans-loading', () => false)
  const inflight = useState<Promise<Plan[]> | null>('plans-inflight', () => null)

  const fetchPlans = async (): Promise<Plan[]> => {
    if (state.value) return state.value
    if (inflight.value) return inflight.value

    loading.value = true
    const p = (async () => {
      try {
        const res = await api.get<{ data: Plan[] }>('/api/public/plans')
        state.value = res?.data ?? []
        return state.value
      } catch (e) {
        if (import.meta.dev) console.warn('[usePlans] fetch failed:', e)
        state.value = []
        return state.value
      } finally {
        loading.value = false
        inflight.value = null
      }
    })()
    inflight.value = p
    return p
  }

  const plans = computed<Plan[]>(() => state.value ?? [])
  /** Plans with a monthly card price — what the public pricing page shows as cards. */
  const selfServePlans = computed<Plan[]>(() => plans.value.filter(p => p.price_aud !== null))
  const planByKey = (key: string): Plan | undefined => plans.value.find(p => p.key === key)

  const formatLimit = (limit: number | null): string => limit === null ? 'Unlimited' : String(limit)
  const formatPrice = (plan: Plan): string => plan.price_aud === null ? 'Contact us' : `AUD $${plan.price_aud}`

  return {
    plans,
    selfServePlans,
    loading,
    fetchPlans,
    planByKey,
    formatLimit,
    formatPrice
  }
}
